import { useNavigate } from '@exam-center/router';
import {
  AddIcon,
  Button,
  ChairAltOutlinedIcon,
  DesktopWindowsOutlinedIcon,
  EditOutlinedIcon,
  Flex,
  Text,
  VisibilityOutlinedIcon,
} from '@shared/components';
import React from 'react';

interface ExamLocationManagementRoomDetailsEditProps {
  isReadonly: boolean;
  isNew: boolean;
  examCenterCode: string;
  examLocationRequestId: string;
  examRoomDetailType: 'physical-info' | 'computer-info';
  examRoomRequestId: string;
  examLocationRequestStatus: 'draft' | 'in-review' | 'rejected';
}

type ExamRoomDetailType = ExamLocationManagementRoomDetailsEditProps['examRoomDetailType'];

const DRAFT_ROUTE_PATHS = {
  'physical-info':
    '/:examCenterCode/exam-location/requests/draft/:requestId/room/:roomRequestId/physical-info',
  'computer-info':
    '/:examCenterCode/exam-location/requests/draft/:requestId/room/:roomRequestId/computer-info',
} as const;

const IN_REVIEW_OR_IN_REJECTED_ROUTE_PATHS = {
  'physical-info': '/:examCenterCode/exam-location/requests/:roomRequestId/physical-info',
  'computer-info': '/:examCenterCode/exam-location/requests/:roomRequestId/computer-info',
} as const;

const ITEM_CONFIG: Record<ExamRoomDetailType, { title: string; icon: React.ReactNode }> = {
  'physical-info': {
    title: 'ข้อมูลลักษณะทางกายภาพ',
    icon: <ChairAltOutlinedIcon />,
  },
  'computer-info': {
    title: 'ข้อมูลเกี่ยวกับอุปกรณ์คอมพิวเตอร์',
    icon: <DesktopWindowsOutlinedIcon />,
  },
};

const getButtonConfig = (isReadonly: boolean, isNew: boolean) => {
  if (isReadonly) {
    return {
      text: 'ดูรายละเอียด',
      icon: <VisibilityOutlinedIcon />,
    };
  }

  return isNew
    ? {
        text: 'กรอกข้อมูล',
        icon: <AddIcon />,
      }
    : {
        text: 'แก้ไข',
        icon: <EditOutlinedIcon />,
      };
};

export default function ExamLocationManagementRoomDetailsEdit({
  isReadonly,
  examCenterCode,
  examLocationRequestId,
  examRoomDetailType,
  examRoomRequestId,
  isNew,
  examLocationRequestStatus,
}: ExamLocationManagementRoomDetailsEditProps) {
  const navigate = useNavigate();

  const { title, icon } = ITEM_CONFIG[examRoomDetailType];
  const buttonConfig = getButtonConfig(isReadonly, isNew);

  const onButtonClick = () => {
    const ROUTE_PATHS =
      examLocationRequestStatus === 'draft'
        ? DRAFT_ROUTE_PATHS
        : IN_REVIEW_OR_IN_REJECTED_ROUTE_PATHS;
    const path = ROUTE_PATHS[examRoomDetailType];
    navigate(path, {
      params: {
        examCenterCode,
        requestId: examLocationRequestId,
        roomRequestId: examRoomRequestId,
      },
    });
  };

  return (
    <Flex
      sx={(theme) => ({
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        p: theme.spacing(4),
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        borderRadius: theme.shape.rounded(2),
      })}
    >
      <Flex sx={(theme) => ({ gap: theme.spacing(4), alignItems: 'center' })}>
        <Flex
          sx={(theme) => ({
            p: theme.spacing(2),
            alignItems: 'center',
            borderRadius: theme.shape.rounded(2),
            backgroundColor: theme.palette.grey[100],
            '& svg': {
              color: theme.palette.grey[600],
              fontSize: 24,
            },
          })}
        >
          {icon}
        </Flex>
        <Text variant='h5'>{title}</Text>
      </Flex>
      <Button
        variant='outlined'
        size='small'
        color='secondary'
        sx={{
          minWidth: 'max-content',
        }}
        startIcon={buttonConfig.icon}
        onClick={onButtonClick}
      >
        {buttonConfig.text}
      </Button>
    </Flex>
  );
}
